import { Sparkles, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';

/**
 * Modal that pitches and triggers a Tier-2 (AI-generated) 3D model upgrade
 * for an existing listing. Pricing differs for the supplier vs designers.
 *
 * Extracted from ListingDetailPage.
 */
export default function UpgradeModelModal({
  open,
  onOpenChange,
  user,
  listing,
  upgradingModel,
  onUpgrade,
}) {
  const isSupplier = user && listing && user.user_id === listing.supplier_id;
  const price = isSupplier ? '0.50' : '1.00';
  const audience = isSupplier ? 'Supplier pricing: $0.50 per model' : 'Designer pricing: $1.00 per model';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-500" />
            Upgrade to AI-Generated Model
          </DialogTitle>
          <DialogDescription>
            Get a photorealistic 3D model generated by AI from your listing photos
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/20 dark:to-purple-950/20 rounded-lg p-4 border border-violet-200 dark:border-violet-800">
            <h4 className="font-semibold mb-2">What you'll get:</h4>
            <ul className="space-y-1 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-violet-500 mt-0.5">✓</span>
                <span>AI-generated photorealistic 3D model</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-violet-500 mt-0.5">✓</span>
                <span>Proper materials, textures, and details</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-violet-500 mt-0.5">✓</span>
                <span>Better visualization for architects</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-violet-500 mt-0.5">✓</span>
                <span>Free for all future downloads</span>
              </li>
            </ul>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <span className="font-medium">One-time upgrade cost:</span>
            <span className="text-2xl font-bold">${price}</span>
          </div>

          <p className="text-xs text-muted-foreground">
            {audience}
            <br />
            Once generated, the AI model is free for everyone to download.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onUpgrade}
            disabled={upgradingModel}
            className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
          >
            {upgradingModel ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Upgrade Now
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
